// "Planned <date>" and the look-back notes. One row per author per period
// (UNIQUE author_id, horizon, period_token). "Planned" is per person: reads are
// filtered to the signed-in person's own row (RLS is author-only too, this is
// defence in depth), so a partner's plan never marks my period planned. A row is SAVED only when notes.savedAt is set — the old
// wizard created rows on open (cadenceDue.ts), and those must not count.

import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { shareInFlight } from '@/lib/sharedRequest'
import { useAuth } from '@/hooks/useAuth'
import { rowKey, serialized } from '@/hooks/usePlanningNotes'

export function monthToken(start: Date): string { return `${start.getFullYear()}-${start.getMonth() + 1}` }
/** The week's first day, unpadded like monthToken: 2026-10-4. */
/** A day's token, in the week's own shape ("2026-10-19" → "2026-10-19" unpadded). */
export function dayToken(day: Date): string { return `${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}` }
export function weekToken(weekStart: Date): string { return `${weekStart.getFullYear()}-${weekStart.getMonth() + 1}-${weekStart.getDate()}` }
/** The plain year, matching cadenceDue's `year:2027`. The season's token is
 *  seasonToken() in lib/cadence/seasons — one definition, not two. */
export function yearToken(year: number): string { return String(year) }

/** The session's notes. `focus` is the week's one line — "This week is
 *  for…" (Scott, 2026-10-03) — kept in the same notes jsonb, no migration. */
export type SessionNotes = { wentWell: string; didnt: string; focus?: string }
type Saved = { at: Date; authorId: string; notes: { wentWell?: string; didnt?: string; focus?: string } }

export type SessionHorizon = 'daily' | 'weekly' | 'monthly' | 'seasonal' | 'annual'

export function usePlanningSession(horizon: SessionHorizon, token: string) {
  const { user } = useAuth()
  const [saved, setSaved] = useState<Saved | null>(null)
  const [mine, setMine] = useState<SessionNotes | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadedToken, setLoadedToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The latest request wins: a slow answer for the month the page has left is dropped.
  const latest = useRef(0)
  // The month on screen now: a save that lands after the page moved on must
  // not mark the NEW month planned.
  const currentToken = useRef(token)
  useEffect(() => { currentToken.current = token }, [token])

  const load = useCallback(async () => {
    const req = ++latest.current
    // No period (the guide bar with nothing to guide): nothing to read.
    if (!token) { setLoading(false); return }
    // Not signed in yet: wait. A read now is thrown away a moment later, when
    // the user arrives and the period is read again.
    if (!user?.id) return
    setLoading(true)
    setLoadedToken(null)
    setError(null)
    // Shared while in flight: Home mounts this week, month and season more
    // than once in a render pass (prod, 2026-10-19: each read four times).
    const { data, error: readError } = await shareInFlight(
      `planning_sessions:${user.id}|${horizon}|${token}`,
      async () => await supabase.from('planning_sessions')
        .select('author_id, updated_at, notes').eq('author_id', user.id).eq('horizon', horizon).eq('period_token', token)
        .order('updated_at', { ascending: false }),
    )
    if (req !== latest.current) return
    if (readError) {
      // Unknown is not empty: opening now would seed blank notes and a save
      // would overwrite the real ones. Stay unloaded; the page offers a retry.
      setError(readError.message)
      setLoading(false)
      return
    }
    const row = (data ?? []).find((r: { notes?: { savedAt?: string } }) => !!r.notes?.savedAt) as
      { author_id: string; updated_at: string; notes: { wentWell?: string; didnt?: string; focus?: string; savedAt: string } } | undefined
    setSaved(row ? { at: new Date(row.notes.savedAt), authorId: row.author_id, notes: row.notes } : null)
    const own = (data ?? []).find((r: { author_id: string; notes?: { savedAt?: string } }) => r.author_id === user?.id && !!r.notes?.savedAt) as
      { notes: { wentWell?: string; didnt?: string; focus?: string } } | undefined
    setMine(own ? { wentWell: own.notes.wentWell ?? '', didnt: own.notes.didnt ?? '', ...(own.notes.focus ? { focus: own.notes.focus } : {}) } : null)
    setLoadedToken(token)
    setLoading(false)
  }, [horizon, token, user?.id])

  useEffect(() => { void load() }, [load])

  const save = useCallback(async (notes: SessionNotes): Promise<boolean> => {
    if (!user?.id) return false
    const uid = user.id
    // The row may carry this period's review by hand (usePeriodReview) and a
    // guided session's notes (usePlanningNotes: its step, energy, weekend…):
    // read it first and keep every key this save does not set; a failed read
    // saves nothing rather than drop them. On the row's one chain, so a
    // guided patch in flight ("Done planning" right after a tap) lands first.
    return serialized(rowKey(uid, horizon, token), async () => {
      const { data: own, error: readError } = await supabase.from('planning_sessions')
        .select('notes').eq('author_id', uid).eq('horizon', horizon).eq('period_token', token).maybeSingle()
      if (readError) return false
      const existing = ((own as { notes?: Record<string, unknown> | null } | null)?.notes ?? {}) as Record<string, unknown>
      const { wentWell: _w, didnt: _d, focus: _f, ...kept } = existing
      const savedAt = new Date().toISOString()
      const { error } = await supabase.from('planning_sessions').upsert(
        { author_id: uid, horizon, period_token: token, notes: { ...kept, ...notes, savedAt }, updated_at: savedAt },
        { onConflict: 'author_id,horizon,period_token' },
      )
      if (error) return false
      if (currentToken.current === token) {
        setSaved({ at: new Date(savedAt), authorId: uid, notes })
        setMine(notes)
      }
      return true
    })
  }, [user?.id, horizon, token])

  return { saved, mine, loading, loadedToken, error, reload: () => { void load() }, save }
}
